import { useState, useEffect, ChangeEvent, useMemo } from 'react'
import Select, { FocusEventHandler } from 'react-select'
import { Box, Flex } from 'theme-ui'
import { CountryCode } from 'types'
import theme from 'theme'
import { ChevronDownIcon } from 'components/Icon'
import { getCountryByCode } from 'components/Form/countryCodes'
import { Input } from './'
import { customSelectStyles } from './styles'
import { noop, onlyNumbers } from 'utils'

export type PhoneCode = CountryCode['value']

export type CountryCodeProps = {
  name: string
  disabled: boolean
  value?: { value: PhoneCode } | undefined
  phoneCodes: { value: PhoneCode }[]
  hasErrors?: boolean
  tabIndex?: number
  onChange?: (selection: { value: PhoneCode }) => void
  onFocus?: FocusEventHandler
  onBlur?: FocusEventHandler
}

export type PhoneNumberProps = {
  name: string
  value?: string | undefined
  disabled?: boolean
  countryCodeValue?: string | undefined
  countryCodes: CountryCode[]
  tabIndex?: number
  hasErrors?: boolean
  onInputChange?: (phone: string) => void
  onFocus?: FocusEventHandler
  onBlur?: FocusEventHandler
}

const DropdownIndicator = () => (
  <Box sx={{ width: 20, height: 20 }}>
    <ChevronDownIcon />
  </Box>
)

export const CountryCodes = ({
  name,
  value,
  disabled,
  phoneCodes,
  onChange,
  onBlur,
  onFocus,
  hasErrors,
  tabIndex,
}: CountryCodeProps) => {
  const [focused, setFocused] = useState(false)
  const handleFocus = ((evt) => {
    setFocused(true)
    onFocus && onFocus(evt)
  }) as FocusEventHandler
  const handleBlur = ((evt) => {
    setFocused(false)
    onBlur && onBlur(evt)
  }) as FocusEventHandler
  return (
    <Box
      sx={{
        p: '10px',
        boxSizing: 'border-box',
        width: '100%',
        bg: 'oldlace',
        borderRadius: 8,
        boxShadow: hasErrors
          ? '0 0 0 2px #f87e0e'
          : focused
          ? '0 0 0 2px #ffc436'
          : 'none',
        opacity: disabled ? 0.6 : 1,
      }}>
      <Select
        id="countryCodes"
        aria-label="select-country-code"
        placeholder="----"
        value={value ?? ''}
        name={name}
        options={phoneCodes}
        getOptionLabel={(option) => `+${option.value}`}
        components={{ DropdownIndicator }}
        styles={customSelectStyles}
        theme={(t) => ({
          ...t,
          cursor: 'pointer',
          colors: {
            ...t.colors,
            primary: theme.colors.primary,
            primary50: theme.colors.darkyellow,
            primary25: theme.colors.oldlace,
          },
        })}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isDisabled={disabled}
        isSearchable
        tabIndex={`${tabIndex}`}
      />
    </Box>
  )
}

export const PhoneNumber = ({
  name,
  value,
  countryCodes,
  countryCodeValue,
  tabIndex = 0,
  disabled = false,
  hasErrors = false,
  onInputChange = noop,
  onFocus,
  onBlur,
}: PhoneNumberProps) => {
  const [phoneCode, setPhoneCode] = useState<{ value: PhoneCode } | undefined>(
    undefined
  )
  const [inputValue, setInputValue] = useState<string>(value ?? '')
  const [countryCode, setCountryCode] = useState<string | undefined>(undefined)
  const phoneCodes = useMemo(() => {
    const phoneCodes = Array.from(
      new Set(countryCodes.map((country) => country.value))
    )
    return phoneCodes.map((value) => ({ value }))
  }, [countryCodes])

  useEffect(() => {
    // Form is in edit mode
    if (value && value.length > 0 && !phoneCode) {
      const parts = value.split(' ')
      // Handle legacy numbers e.g. +447849, 447849
      if (parts.length > 1) {
        const initialCountryPhoneCode = parts[0].replace('+', '')
        setInputValue(parts[1])
        setPhoneCode({ value: initialCountryPhoneCode })
      } else {
        const number = parts[0]
        setInputValue(number.replace('+', ''))
      }
    }
    // eslint-disable-next-line
  }, [value])

  useEffect(() => {
    if (
      countryCodeValue &&
      countryCodeValue.length > 0 &&
      countryCodeValue !== countryCode
    ) {
      setCountryCode(countryCodeValue)
      setPhoneCode(getCountryByCode(countryCodeValue))
    }
    // eslint-disable-next-line
  }, [countryCodeValue])

  function handleCountryOnChange(phoneCodeSelection: { value: PhoneCode }) {
    setPhoneCode(phoneCodeSelection)
    if (inputValue) {
      onInputChange(`+${phoneCodeSelection.value} ${inputValue}`)
    }
  }

  function handleInputOnChange(e: ChangeEvent<HTMLInputElement>) {
    const newValue = onlyNumbers(e.target.value)
    setInputValue(newValue)
    if (phoneCode && newValue.length > 0) {
      onInputChange(`+${phoneCode.value} ${newValue}`)
    }

    if (!phoneCode || newValue.length === 0) {
      onInputChange('')
    }
  }

  const handleFocus = ((evt) => {
    onFocus && onFocus(evt)
  }) as FocusEventHandler
  const handleBlur = ((evt) => {
    onBlur && onBlur(evt)
  }) as FocusEventHandler

  return (
    <>
      <Flex
        sx={{
          position: 'relative',
          width: '100%',
          alignItems: 'flex-start',
          opacity: disabled ? 0.6 : 1,
        }}>
        <Box sx={{ pr: 4, width: 117 }}>
          <CountryCodes
            name={`${name}-countryCode`}
            phoneCodes={phoneCodes}
            value={phoneCode}
            disabled={disabled}
            hasErrors={hasErrors}
            onChange={handleCountryOnChange}
            tabIndex={tabIndex}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Input
            name={`${name}-number`}
            aria-label="phone-number"
            aria-invalid={hasErrors}
            placeholder="Phone number"
            id="phoneNumber"
            sx={{ width: '100%' }}
            value={inputValue}
            tabIndex={tabIndex + 0.1}
            disabled={disabled}
            onChange={handleInputOnChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </Box>
      </Flex>
    </>
  )
}
